import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ReportingRow, type ReportingStatus } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Loading, Panel, StatTile, Th, fmtDate, fmtPct, playerLabel } from './shared';

// Plan 050 — who reports how often, who needs one more report.

const STATUS_META: Record<ReportingStatus, { label: string; chip: string }> = {
  ok: { label: 'OK', chip: 'border-emerald-800 bg-emerald-950/40 text-emerald-200' },
  at_risk: { label: 'At risk', chip: 'border-amber-800 bg-amber-950/40 text-amber-200' },
  missing: { label: 'Not comparable', chip: 'border-red-900 bg-red-950/40 text-red-200' },
};

type SortKey = 'status' | 'name' | 'last' | 'gap' | 'reports30d';
const STATUS_ORDER: Record<ReportingStatus, number> = { at_risk: 0, missing: 1, ok: 2 };

export default function ReportingTab() {
  const q = useQuery({ queryKey: ['insights', 'reporting'], queryFn: api.getAdminReporting });
  const [sort, setSort] = useState<SortKey>('status');
  const [copied, setCopied] = useState(false);

  const rows = useMemo(() => {
    const list = [...(q.data?.players ?? [])];
    const days = (r: ReportingRow) => r.daysSinceReport ?? Infinity;
    list.sort((a, b) => {
      switch (sort) {
        case 'name':
          return playerLabel(a.player).localeCompare(playerLabel(b.player));
        case 'last':
          return days(b) - days(a) || 0;
        case 'gap':
          return (b.medianGapDays ?? Infinity) - (a.medianGapDays ?? Infinity) || 0;
        case 'reports30d':
          return a.reports30d - b.reports30d;
        default:
          return STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || days(b) - days(a) || 0;
      }
    });
    return list;
  }, [q.data, sort]);

  if (q.isLoading || !q.data) return <Loading />;
  const data = q.data;
  const atRisk = data.players.filter((r) => r.status === 'at_risk');

  const copyForChat = async () => {
    const deadline = fmtDate(atRisk[0]?.reportByIso ?? data.nextBoundaryIso);
    const text =
      `Please report your power before ${deadline} to be ranked in the monthly strength push: ` +
      atRisk.map((r) => playerLabel(r.player)).join(', ');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this text:', text);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        Strength statistics only rank players with a report within{' '}
        {data.maxGapDays} days of each month boundary. Next boundary:{' '}
        <span className="text-slate-200">{fmtDate(data.nextBoundaryIso)}</span>. At-risk
        players with push enabled get a reminder 5 days before.
      </p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label="Reported in last 14 days"
          value={fmtPct(data.alliance.share14d)}
          sub={`of ${data.alliance.activePlayers} active players`}
        />
        <StatTile
          label="Median gap between reports"
          value={data.alliance.medianGapDays == null ? '—' : `${data.alliance.medianGapDays.toFixed(1)} d`}
          sub="last 90 days"
        />
        <StatTile
          label="At risk"
          value={data.alliance.counts.atRisk}
          tone={data.alliance.counts.atRisk > 0 ? 'warn' : 'default'}
          sub="one more report needed"
        />
        <StatTile
          label="Not comparable"
          value={data.alliance.counts.missing}
          tone={data.alliance.counts.missing > 0 ? 'bad' : 'default'}
          sub="this month"
        />
      </div>

      {data.alliance.monthly.length > 0 && (
        <Panel title="Comparable at month start">
          <div className="flex flex-wrap gap-3 text-sm">
            {data.alliance.monthly.map((m) => (
              <div key={m.month} className="min-w-[90px]">
                <div className="text-[11px] text-slate-500">{m.label}</div>
                <div className="tabular-nums text-slate-100">{fmtPct(m.comparableShare)}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {atRisk.length > 0 && (
        <Panel
          title={`At risk (${atRisk.length})`}
          actions={
            <button
              type="button"
              onClick={copyForChat}
              className="rounded border border-slate-700 px-2 py-0.5 text-xs hover:bg-slate-800"
            >
              {copied ? 'Copied ✓' : 'Copy for chat'}
            </button>
          }
        >
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-amber-200">
            {atRisk.map((r) => (
              <span key={r.player.id}>
                {playerLabel(r.player)}
                <span className="ml-1 text-[11px] text-slate-500">
                  by {fmtDate(r.reportByIso)}
                </span>
              </span>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="All players">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <Th onClick={() => setSort('name')} active={sort === 'name'} left>
                  Player
                </Th>
                <Th onClick={() => setSort('status')} active={sort === 'status'} left>
                  Status
                </Th>
                <Th onClick={() => setSort('last')} active={sort === 'last'}>
                  Last report
                </Th>
                <Th onClick={() => setSort('gap')} active={sort === 'gap'}>
                  Median gap
                </Th>
                <Th onClick={() => setSort('reports30d')} active={sort === 'reports30d'}>
                  Reports 30d
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.player.id} className="border-t border-slate-800/60">
                  <td className="px-2 py-1">{playerLabel(r.player)}</td>
                  <td className="px-2 py-1">
                    <span
                      className={cn(
                        'rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                        STATUS_META[r.status].chip,
                      )}
                    >
                      {STATUS_META[r.status].label}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                    {fmtDate(r.lastReportAt)}
                    {r.daysSinceReport != null && (
                      <span className="ml-1 text-xs text-slate-500">
                        ({Math.floor(r.daysSinceReport)} d)
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                    {r.medianGapDays == null ? '—' : `${r.medianGapDays.toFixed(1)} d`}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                    {r.reports30d}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
